/* Quick editing for the selected design (0.3e):
   - SelectionOverlay: a small size label attached to the selection ("28.5 × 33.5 cm", in the customer's
     unit) and, on desktop and tablet, a floating toolbar (width, lock, height, Duplicate, Delete, More).
   - SelectionMenu: the right-click menu (desktop), and the long-press actions (touch: a menu at the
     finger on tablets, a bottom sheet on phones).
   - PhoneEditBar + SizeSheet: on phones, a bottom edit bar instead of a floating toolbar.
   Every action runs the same command as the Selected design panel (selectionCommands.ts), so there is
   one set of editing rules, one undo step per change, and autosave as usual. */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { type Item } from '@gsb/core';
import { useStore, select, layerOrder } from '../store/store';
import { Icon, type IconName } from '../ui/icons';
import { NumberField, BottomSheet } from '../ui/primitives';
import {
  selectedItems, resizeSelection, rotateSelection, flipSelection, toggleLockPosition, resetSelection, resetState,
  duplicateSelection, deleteSelection, layerSelection, toggleProportionLock, openSelectionPanel
} from './selectionCommands';

/* the phone Size sheet is opened from the edit bar and from the long-press actions */
const useTools = create<{ sizeOpen: boolean }>(() => ({ sizeOpen: false }));
const openSize = () => useTools.setState({ sizeOpen: true });

/** a rectangle in canvas pixels (the .stage element) */
export interface PxRect { x: number; y: number; w: number; h: number }

/* ================================================================ overlay */

interface OverlayProps {
  /** the selection's outline on screen (turned copies: their upright bounding box) */
  rect: PxRect;
  /** the canvas size */
  size: { w: number; h: number };
  /** the label's text */
  label: string;
  /** something is being dragged, resized or turned: label only, live */
  moving: boolean;
  /** show the floating toolbar (desktop and tablet; not while a menu is open) */
  toolbar: boolean;
  rulers: boolean;
  /** touch screens: bigger handles, so a little more room */
  coarse: boolean;
  /** where the rotate handle is (canvas pixels): above the outline, or below it for a copy turned 180° */
  rot: { x: number; y: number } | null;
}

/** Where things go: the label under the selection; the toolbar under the label (or above the rotate
    handle when there's no room below, or pinned to the top of the canvas); always inside the canvas, clear
    of the rulers, the sheet chip at the top and the zoom bar at the bottom. */
export function SelectionOverlay({ rect, size, label, moving, toolbar, rulers, coarse, rot }: OverlayProps) {
  const sel = useStore(s => s.selection);
  const lab = useRef<HTMLSpanElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ lw: 110, lh: 24, tw: 330, th: 46 });
  useLayoutEffect(() => {
    const lw = lab.current?.offsetWidth || dims.lw, lh = lab.current?.offsetHeight || dims.lh;
    const tw = bar.current?.offsetWidth || dims.tw, th = bar.current?.offsetHeight || dims.th;
    if (lw !== dims.lw || lh !== dims.lh || tw !== dims.tw || th !== dims.th) setDims({ lw, lh, tw, th });
  });
  const left0 = (rulers ? 22 : 0) + 6, right0 = size.w - 6;
  const top0 = (rulers ? 22 : 0) + 52, bottom0 = size.h - 66;
  // nothing to show when the selection is scrolled out of view
  if (rect.x > right0 || rect.x + rect.w < left0 || rect.y > size.h || rect.y + rect.h < 0) return null;
  const handle = coarse ? 11 : 7;
  const cx = rect.x + rect.w / 2;
  // a narrow canvas (portrait tablet): a slimmer toolbar that still fits
  const narrow = right0 - left0 < 470;
  const clampX = (w: number) => Math.round(w >= right0 - left0 ? left0 : Math.min(Math.max(cx - w / 2, left0), right0 - w));
  // keep clear of the outline, its handles and the rotate handle (wherever that is)
  const R = 15;
  const ko = {
    y0: Math.min(rect.y - handle, rot ? rot.y - R : Infinity),
    y1: Math.max(rect.y + rect.h + handle, rot ? rot.y + R : -Infinity)
  };
  // the label: below, else above, else just inside the bottom edge
  let ly: number, at: 'below' | 'above' | 'inside';
  if (ko.y1 + 2 + dims.lh <= bottom0) { ly = ko.y1 + 2; at = 'below'; }
  else if (ko.y0 - 4 - dims.lh >= top0) { ly = ko.y0 - 4 - dims.lh; at = 'above'; }
  else { ly = Math.max(top0, Math.min(rect.y + rect.h - dims.lh - 6, bottom0 - dims.lh)); at = 'inside'; }
  // the toolbar: under the label, else above everything, else pinned to the top or bottom of the canvas,
  // whichever keeps off the rotate handle
  let ty: number | null = null;
  if (toolbar && !moving) {
    const below = (at === 'below' ? ly + dims.lh : ko.y1) + 6;
    const above = (at === 'above' ? ly : ko.y0) - 6 - dims.th;
    const clearOfRot = (y: number) => !rot || rot.y + R < y || rot.y - R > y + dims.th;
    ty = below + dims.th <= bottom0 ? below : above >= top0 ? above : clearOfRot(top0) ? top0 : clearOfRot(bottom0 - dims.th) ? bottom0 - dims.th : top0;
  }
  return (
    <div className="sel-ui" aria-live="off">
      <span ref={lab} className={'sel-size num' + (moving ? ' is-live' : '')} style={{ left: clampX(dims.lw), top: Math.round(ly) }}>{label}</span>
      {ty !== null && (
        <div ref={bar} className={'qt' + (narrow ? ' is-narrow' : '')} role="toolbar" aria-label="Edit the selected design" style={{ left: clampX(dims.tw), top: Math.round(ty) }}
          onPointerDown={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
          {/* keyed by the selection, like the panel: a size typed for one design never lands on another */}
          <QuickBar key={sel.join()} />
        </div>
      )}
    </div>
  );
}

/** the toolbar's buttons for what's selected */
function QuickBar() {
  const sel = useStore(s => s.selection);
  const doc = useStore(s => s.doc);
  const unit = doc.settings.unit;
  const lock = useStore(s => s.lockAspect);
  const items = doc.sheets.flatMap(s => s.items).filter(i => sel.includes(i.id));
  if (!items.length) return null;
  const ids = items.map(i => i.id);
  const one = items.length === 1 ? items[0] : null;
  const allLocked = items.every(i => i.locked);
  const more = <button type="button" className="qt-more" data-tip="More options: the full Selected design panel" onClick={() => openSelectionPanel()}><Icon name="more" /><span>More</span></button>;
  const dupDel = (
    <>
      <button type="button" className="ibtn qt-b" aria-label="Duplicate" data-tip="Duplicate (Ctrl/⌘ D)" onClick={() => void duplicateSelection(1)}><Icon name="copy" /></button>
      <button type="button" className="ibtn qt-b is-danger" aria-label="Delete" data-tip="Delete (Delete key)" onClick={deleteSelection}><Icon name="trash" /></button>
    </>
  );
  if (allLocked) {
    return (
      <>
        <span className="qt-txt"><Icon name="lock" />Locked in place</span>
        <button type="button" className="btn btn-line btn-xs" onClick={() => toggleLockPosition(ids)}><Icon name="unlock" />Unlock</button>
        <span className="qt-sep" />{more}
      </>
    );
  }
  if (!one) {
    return <><span className="qt-txt num">{items.length} selected</span><span className="qt-sep" />{dupDel}<span className="qt-sep" />{more}</>;
  }
  return (
    <>
      <label className="qt-f"><em>W</em><NumberField value={one.widthMm} unit={unit} mmValue min={3} max={5000} ariaLabel="Width" small onCommit={v => resizeSelection(ids, v, null)} /></label>
      <button type="button" className="ibtn qt-b qt-lock" aria-pressed={lock} aria-label={lock ? 'Proportions locked' : 'Proportions unlocked'}
        data-tip={lock ? 'Proportions locked: width and height change together. Click to unlock' : 'Proportions unlocked: width and height change separately. Click to lock'} onClick={toggleProportionLock}>
        <Icon name={lock ? 'lock' : 'unlock'} />
      </button>
      <label className="qt-f"><em>H</em><NumberField value={one.heightMm} unit={unit} mmValue min={3} max={5000} ariaLabel="Height" small onCommit={v => resizeSelection(ids, null, v)} /></label>
      <span className="qt-sep" />{dupDel}<span className="qt-sep" />{more}
    </>
  );
}

/* ================================================================ the menu */

interface MenuAction { key: string; label: string; icon: IconName; kbd?: string; danger?: boolean; run: () => void }

/** The actions for the selection, in menu order (groups split by null). Only what applies is listed. */
function menuActions(opts: { phone: boolean; editSize: () => void }): (MenuAction | null | string)[] {
  const items = selectedItems();
  if (!items.length) return [];
  const ids = items.map(i => i.id);
  const allLocked = items.every(i => i.locked);
  const r = resetState(ids);
  const out: (MenuAction | null | string)[] = [];
  if (!allLocked) out.push({ key: 'size', label: 'Edit measurements…', icon: 'measure', run: opts.editSize });
  out.push(
    { key: 'dup', label: 'Duplicate', icon: 'copy', kbd: 'Ctrl D', run: () => void duplicateSelection(1) },
    { key: 'del', label: 'Delete', icon: 'trash', kbd: 'Del', danger: true, run: deleteSelection }
  );
  if (!allLocked) {
    out.push(null,
      { key: 'rotr', label: 'Rotate 90° right', icon: 'rotCw', run: () => rotateSelection(ids, 90) },
      { key: 'rotl', label: 'Rotate 90° left', icon: 'rotCcw', run: () => rotateSelection(ids, -90) },
      { key: 'fliph', label: 'Flip horizontally', icon: 'flipH', run: () => flipSelection(ids, 'x') },
      { key: 'flipv', label: 'Flip vertically', icon: 'flipV', run: () => flipSelection(ids, 'y') });
  }
  // forward / backward only when there's something to go in front of or behind
  const s = useStore.getState(), order = s.doc.sheets[s.activeSheet]?.items.map(i => i.id) || [];
  const now = order.join();
  const fwd = layerOrder(order, ids, 'forward').join() !== now, back = layerOrder(order, ids, 'backward').join() !== now;
  if (fwd || back) out.push(null);
  if (fwd) out.push({ key: 'fwd', label: 'Bring forward', icon: 'forward', run: () => layerSelection('forward') });
  if (back) out.push({ key: 'back', label: 'Send backward', icon: 'backward', run: () => layerSelection('backward') });
  if (!allLocked && (r.proportions || r.rotation)) {
    out.push(null, 'Reset');
    if (r.proportions) out.push({ key: 'rprop', label: 'Reset proportions', icon: 'reset', run: () => resetSelection(ids, { proportions: true }) });
    if (r.rotation) out.push({ key: 'rrot', label: 'Reset rotation', icon: 'reset', run: () => resetSelection(ids, { rotation: true }) });
    if (r.proportions && r.rotation) out.push({ key: 'rboth', label: 'Reset both', icon: 'reset', run: () => resetSelection(ids, { proportions: true, rotation: true }) });
  }
  out.push(null,
    { key: 'lock', label: allLocked ? 'Unlock position' : 'Lock position', icon: allLocked ? 'unlock' : 'lock', run: () => toggleLockPosition(ids) },
    { key: 'more', label: 'More options', icon: 'panel', run: () => openSelectionPanel() });
  return out;
}

/**
 * The right-click / long-press menu. `at`: where it was opened (window pixels). On phones it's a bottom
 * sheet with big rows; elsewhere a menu at the pointer, moved in from the window's edges so it fits.
 */
export function SelectionMenu({ at, phone, onClose }: { at: { x: number; y: number }; phone: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: at.x, top: at.y, ready: false });
  const sel = useStore(s => s.selection);
  // measure, then fit inside the window
  useLayoutEffect(() => {
    if (phone || !ref.current) return;
    const w = ref.current.offsetWidth, h = Math.min(ref.current.offsetHeight, window.innerHeight - 16);
    const left = at.x + w > window.innerWidth - 8 ? Math.max(8, at.x - w) : at.x;
    const top = at.y + h > window.innerHeight - 8 ? Math.max(8, window.innerHeight - 8 - h) : at.y;
    setPos({ left, top, ready: true });
  }, [at.x, at.y, phone]);
  // close on a click elsewhere, Esc, scrolling or zooming the canvas, leaving the window, or nothing selected
  useEffect(() => {
    if (phone) return;
    const down = (e: Event) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    const away = () => onClose();
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('keydown', key, true);
    window.addEventListener('wheel', away, { passive: true });
    window.addEventListener('resize', away);
    window.addEventListener('blur', away);
    return () => {
      document.removeEventListener('pointerdown', down, true); document.removeEventListener('keydown', key, true);
      window.removeEventListener('wheel', away); window.removeEventListener('resize', away); window.removeEventListener('blur', away);
    };
  }, [phone]);
  useEffect(() => { if (!sel.length) onClose(); }, [sel.length]);
  // keyboard focus on the first item once the menu is placed and visible (arrow keys then move in the menu,
  // not the design)
  useEffect(() => { if (pos.ready) (ref.current?.querySelector('[role="menuitem"]') as HTMLElement | null)?.focus(); }, [pos.ready]);

  const editSize = () => {
    if (phone) { openSize(); return; }
    // the quick toolbar comes back once the menu has closed: its width field, else the panel's
    window.setTimeout(() => {
      const input = document.querySelector('.qt input') as HTMLInputElement | null;
      if (input) input.focus(); else openSelectionPanel('width');
    }, 80);
  };
  const acts = menuActions({ phone, editSize });
  const run = (a: MenuAction) => { onClose(); a.run(); };
  // arrow keys move between the items
  const keys = (e: React.KeyboardEvent) => {
    // keys pressed in the menu are for the menu: never the canvas shortcuts (arrows nudge, Delete removes)
    if (e.key !== 'Escape') e.stopPropagation();
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const list = [...(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [])];
    const i = list.indexOf(document.activeElement as HTMLElement);
    list[(i + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length]?.focus();
  };

  if (phone) {
    return (
      <BottomSheet label="Design actions" className="bs-actions" onClose={onClose}>
        <div className="acts-list" role="menu" aria-label="Design actions">
          {acts.filter(a => a && typeof a !== 'string').map(a => {
            const x = a as MenuAction;
            return <button key={x.key} type="button" role="menuitem" data-act={x.key} className={'acts-it' + (x.danger ? ' is-danger' : '')} onClick={() => run(x)}><Icon name={x.icon} />{x.label}</button>;
          })}
        </div>
      </BottomSheet>
    );
  }
  return (
    <div ref={ref} className="cmenu" role="menu" aria-label="Design actions" style={{ left: pos.left, top: pos.top, visibility: pos.ready ? 'visible' : 'hidden' }}
      onKeyDown={keys} onContextMenu={e => e.preventDefault()}>
      {acts.map((a, i) => a === null ? <hr key={'s' + i} /> : typeof a === 'string' ? <span key={'g' + i} className="cmenu-g">{a}</span> : (
        <button key={a.key} type="button" role="menuitem" data-act={a.key} className={'cmenu-it' + (a.danger ? ' is-danger' : '')} onClick={() => run(a)}>
          <Icon name={a.icon} /><span>{a.label}</span>{a.kbd && <kbd>{a.kbd}</kbd>}
        </button>
      ))}
    </div>
  );
}

/* ================================================================ phones */

/** On phones, while something is selected: the edit bar takes the place of the bottom tabs. */
export function PhoneEditBar() {
  const sel = useStore(s => s.selection);
  const sheets = useStore(s => s.doc.sheets);
  const items = sheets.flatMap(x => x.items).filter(i => sel.includes(i.id));
  const sizeOpen = useTools(s => s.sizeOpen);
  if (!items.length) return null;
  const ids = items.map(i => i.id);
  const allLocked = items.every(i => i.locked);
  const btn = (key: string, icon: IconName, label: string, run: () => void, extra = '') => (
    <button key={key} type="button" className={'eb-b' + extra} data-eb={key} onClick={run}><Icon name={icon} /><span>{label}</span></button>
  );
  return (
    <>
      <nav className="ebar" aria-label="Edit the selected design">
        {allLocked
          ? btn('unlock', 'unlock', 'Unlock', () => toggleLockPosition(ids))
          : btn('size', 'measure', 'Size', openSize)}
        {btn('dup', 'copy', 'Duplicate', () => void duplicateSelection(1))}
        {!allLocked && btn('rot', 'rotCw', 'Rotate', () => rotateSelection(ids, 90))}
        {btn('del', 'trash', 'Delete', deleteSelection, ' is-danger')}
        {btn('more', 'more', 'More', () => openSelectionPanel())}
        {btn('done', 'check', 'Done', () => select([]), ' is-done')}
      </nav>
      {sizeOpen && <SizeSheet items={items} onClose={() => useTools.setState({ sizeOpen: false })} />}
    </>
  );
}

/** Width and height on the number keypad, with the proportions lock. */
function SizeSheet({ items, onClose }: { items: Item[]; onClose: () => void }) {
  const unit = useStore(s => s.doc.settings.unit);
  const lock = useStore(s => s.lockAspect);
  const name = useStore(s => s.doc.designs.find(d => d.id === items[0]?.designId)?.name);
  const ids = items.map(i => i.id);
  const first = items[0];
  useEffect(() => { if (!items.length) onClose(); }, [items.length]);
  if (!first) return null;
  return (
    <BottomSheet label="Size" className="bs-size" onClose={onClose}>
      <div className="size-sheet">
        <div className="size-h"><b>Size</b><span className="muted small">{items.length > 1 ? items.length + ' selected' : name}</span></div>
        <div className="size-grid">
          <NumberField label="Width" value={first.widthMm} unit={unit} mmValue min={3} max={5000} onCommit={v => resizeSelection(ids, v, null)} />
          <button type="button" className="ibtn size-lock" aria-pressed={lock} aria-label={lock ? 'Proportions locked' : 'Proportions unlocked'} onClick={toggleProportionLock}><Icon name={lock ? 'lock' : 'unlock'} /></button>
          <NumberField label="Height" value={first.heightMm} unit={unit} mmValue min={3} max={5000} onCommit={v => resizeSelection(ids, null, v)} />
        </div>
        <p className="tiny muted">{lock ? 'Proportions locked: change one and the other follows.' : 'Proportions unlocked: width and height change separately.'}{items.length > 1 ? ' Applies to all ' + items.length + '.' : ''}</p>
        <button type="button" className="btn btn-primary btn-block" onClick={onClose}>Done</button>
      </div>
    </BottomSheet>
  );
}

